// reCAPTCHA v3 Enterprise Service para VettConsult
// Este servicio maneja la integración con Google reCAPTCHA v3 Enterprise

// Interfaz para respuesta de reCAPTCHA (utilizada por el backend)
export interface RecaptchaResponse {
  success: boolean;
  score: number;
  action: string;
  challenge_ts: string;
  hostname: string;
  "error-codes"?: string[];
}

// Tipos de acciones predefinidas para reCAPTCHA v3
export const RECAPTCHA_ACTIONS = {
  LOGIN: "LOGIN",
  SIGNUP: "SIGNUP",
  CONTACT: "CONTACT",
  CONTACT_FORM: "contact_form",
  APPOINTMENT_CREATE: "appointment_create",
  APPOINTMENT_RESERVE: "appointment_reserve",
  PASSWORD_RESET: "password_reset",
  EMAIL_VERIFICATION: "email_verification",
  PROFILE_UPDATE: "profile_update",
} as const;

type RecaptchaAction =
  (typeof RECAPTCHA_ACTIONS)[keyof typeof RECAPTCHA_ACTIONS];

class RecaptchaService {
  private siteKey: string;
  private isLoaded: boolean = false;
  private loadPromise: Promise<void> | null = null;
  private debug: boolean = false;

  constructor() {
    // Usar la clave Enterprise proporcionada
    this.siteKey =
      import.meta.env.VITE_RECAPTCHA_SITE_KEY ||
      "6LdN2WQrAAAAAI5ZkJcxpTRz_Xp9BK8vnCB6Jw4u";
    this.debug = import.meta.env.DEV || false;

    if (this.debug) {
    }
  }

  /**
   * Cargar el script de reCAPTCHA v3 Enterprise
   */
  private loadRecaptchaScript(): Promise<void> {
    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = new Promise((resolve, reject) => {
      if (
        this.isLoaded ||
        document.querySelector('script[src*="recaptcha/enterprise.js"]')
      ) {
        this.isLoaded = true;
        if (this.debug) {
        }
        resolve();
        return;
      }

      if (!this.siteKey) {
        reject(new Error("reCAPTCHA site key not configured"));
        return;
      }

      const script = document.createElement("script");
      // Usar la URL de Enterprise
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${this.siteKey}`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        this.isLoaded = true;
        if (this.debug) {
        }
        resolve();
      };

      script.onerror = (error) => {
        console.error(
          "[RECAPTCHA] Failed to load reCAPTCHA Enterprise script:",
          error
        );
        reject(new Error("Failed to load reCAPTCHA Enterprise script"));
      };

      document.head.appendChild(script);
    });

    return this.loadPromise;
  }

  /**
   * Inicializar reCAPTCHA de forma temprana (llamar en el componente raíz)
   */
  async initialize(): Promise<void> {
    try {
      await this.loadRecaptchaScript();
      if (this.debug) {
      }
    } catch (error) {
      console.error("[RECAPTCHA] Failed to initialize:", error);
    }
  }

  /**
   * Ejecutar reCAPTCHA v3 Enterprise para una acción específica
   */
  async executeRecaptcha(action: RecaptchaAction | string): Promise<string> {
    if (this.debug) {
    }

    try {
      await this.loadRecaptchaScript();

      if (!window.grecaptcha || !window.grecaptcha.enterprise) {
        throw new Error("reCAPTCHA Enterprise not loaded");
      }

      return new Promise((resolve, reject) => {
        window.grecaptcha.enterprise.ready(() => {
          window.grecaptcha.enterprise
            .execute(this.siteKey, { action })
            .then((token: string) => {
              if (this.debug) {
              }
              resolve(token);
            })
            .catch((error: any) => {
              console.error(
                `[RECAPTCHA] Error executing action ${action}:`,
                error
              );
              reject(error);
            });
        });
      });
    } catch (error) {
      console.error(
        `[RECAPTCHA] Error executing reCAPTCHA for action ${action}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Ejecutar reCAPTCHA con retry en caso de fallo
   */
  async executeWithRetry(
    action: RecaptchaAction | string,
    maxRetries: number = 2
  ): Promise<string> {
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const token = await this.executeRecaptcha(action);
        return token;
      } catch (error) {
        lastError = error as Error;
        if (this.debug) {
          console.warn(
            `[RECAPTCHA] Attempt ${attempt} failed for action ${action}:`,
            error
          );
        }

        if (attempt < maxRetries) {
          // Esperar un poco antes del retry
          await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
        }
      }
    }

    throw (
      lastError ||
      new Error(`Failed to execute reCAPTCHA after ${maxRetries} attempts`)
    );
  }

  /**
   * Verificar si reCAPTCHA está configurado correctamente
   */
  isConfigured(): boolean {
    return !!this.siteKey;
  }

  /**
   * Verificar si reCAPTCHA está cargado y listo
   */
  isReady(): boolean {
    return this.isLoaded && !!window.grecaptcha?.enterprise;
  }

  /**
   * Obtener la clave del sitio
   */
  getSiteKey(): string {
    return this.siteKey;
  }

  /**
   * Habilitar/deshabilitar modo debug
   */
  setDebug(enabled: boolean): void {
    this.debug = enabled;
  }

  /**
   * Obtener información del estado del servicio
   */
  getStatus(): {
    configured: boolean;
    loaded: boolean;
    ready: boolean;
    siteKey: string;
  } {
    return {
      configured: this.isConfigured(),
      loaded: this.isLoaded,
      ready: this.isReady(),
      siteKey: this.siteKey,
    };
  }
}

// Declaración de tipos para window.grecaptcha Enterprise
declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (
        siteKey: string,
        options: { action: string }
      ) => Promise<string>;
      enterprise: {
        ready: (callback: () => void) => void;
        execute: (
          siteKey: string,
          options: { action: string }
        ) => Promise<string>;
      };
    };
  }
}

export const recaptchaService = new RecaptchaService();
export default recaptchaService;
